import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [availablePlans, setAvailablePlans] = useState(null);
  const [environment, setEnvironment] = useState('production');
  const [activeSub, setActiveSub] = useState(null);
  const [announcements, setAnnouncements] = useState(null);
  const [betslip, setBetslip] = useState(false);
  const [gift, setGift] = useState(false);

  // Check authentication status on mount and when visibility changes
  useEffect(() => {
    checkAuth();

    // Re-check auth when tab becomes visible (user might have logged in from another tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkAuth();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const checkAuth = async () => {
    try {
      // Check for testmode URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const isTestMode = urlParams.get('testmode') === 'true';

      const url = isTestMode
        ? '/api/auth/session?env=test'
        : '/api/auth/session';

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // Important: Include cookies for session
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const isAuth = data.authenticated || false;
        setIsAuthenticated(isAuth);
        setUser(data.user || null);
        setActiveSub(data.activeSub || null);
        setAvailablePlans(data.availablePlans || null);
        setEnvironment(data.environment || 'production');
        setAnnouncements(data.announcements || null);
        setBetslip(data.betslip || false);
        setGift(data.gift || false);

        if (data.authenticated) {
          localStorage.setItem('so_was_authenticated', '1');
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setActiveSub(null);
        setAvailablePlans(null);
        setEnvironment('production');
        setAnnouncements(null);
        setBetslip(false);
        setGift(false);
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
      setIsAuthenticated(false);
      setUser(null);
      setActiveSub(null);
      setAvailablePlans(null);
      setEnvironment('production');
      setAnnouncements(null);
      setBetslip(false);
      setGift(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = () => {
    // Redirect to backend OAuth login endpoint
    // The backend will handle the OAuth flow and redirect back to the app
    window.location.href = '/api/auth/login';
  };

  const logout = async () => {
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setIsAuthenticated(false);
        setUser(null);
        // Optionally redirect to home page
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    activeSub,
    availablePlans,
    environment,
    announcements,
    betslip,
    gift,
    login,
    logout,
    checkAuth, // Expose checkAuth in case we need to manually refresh
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
